import { useEffect, useRef, useState, type FormEvent } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useApi, describeError } from "../hooks/useApi";
import { getAuthProviders } from "../lib/api";
import { describeLoginError, googleStartUrl, resolveLoginRedirect } from "../lib/authRedirect";
import type { AuthProviders } from "../types";

/**
 * `/login` — e-mail + password, plus Google when the server has it configured.
 *
 * No registration and no "forgot password": accounts are created by hand
 * (`uv run jarvis user create`), so there is nothing here to offer instead.
 *
 * Where a password login lands on success is `App`'s decision, not this
 * page's: once `login()` resolves, `status` flips to "authenticated" and
 * `App` re-renders past this screen entirely (see `resolveLoginRedirect` in
 * `App.tsx`) — so a successful submit never has to navigate anywhere itself.
 * Google is different: that flow leaves the SPA entirely for a real 302 to
 * Google and back, so this page has to hand it a return path up front (the
 * same one `App` would have picked) rather than letting `login()` decide.
 */
export default function LoginPage() {
  const { login } = useAuth();
  const emailRef = useRef<HTMLInputElement>(null);
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Google button only when the backend actually has a client id — a 503 or
  // an offline backend both mean "don't show it", not "show it disabled".
  const providers = useApi<AuthProviders>(() => getAuthProviders(), []);
  const returnTo = resolveLoginRedirect(location.state);

  // `?error=` is a one-shot signal from the Google callback redirect, not
  // page state to keep in sync — read it once, then drop it from the URL so
  // a refresh does not replay the same message (same pattern as `?period=`
  // and `?account=` on TransactionsPage).
  const [params, setParams] = useSearchParams();
  const [oauthError] = useState<string | null>(() => describeLoginError(params.get("error")));
  useEffect(() => {
    if (!params.has("error")) return;
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete("error");
        return next;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    emailRef.current?.focus();
  }, []);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await login(email, password);
      // Success unmounts this page (see the class doc above) — nothing left
      // to do, and resetting `busy` here would only flash the button enabled
      // for the one frame before that happens.
    } catch (err) {
      setError(describeError(err).message);
      setBusy(false);
    }
  }

  function handleGoogleClick() {
    // A real navigation, not a fetch: the backend's 302 chain through Google
    // and back needs the whole browser, and it ends with the session cookie
    // already set, so there is nothing left for this page to do afterward.
    window.location.assign(googleStartUrl(returnTo));
  }

  // A password error from just now always wins over a stale `?error=` from
  // the redirect that brought the user to this page.
  const displayError = error ?? oauthError;

  return (
    <div className="grid h-full place-items-center px-4 py-8">
      <form
        onSubmit={(event) => void handleSubmit(event)}
        className="jv-card w-full max-w-sm p-4 sm:p-5"
      >
        <div className="flex items-center gap-2.5">
          <span
            aria-hidden="true"
            className="grid size-8 shrink-0 place-items-center rounded-control bg-accent/15 font-mono text-title font-semibold text-accent"
          >
            J
          </span>
          <span className="text-sm font-semibold tracking-tight">Jarvis</span>
        </div>

        <h1 className="mt-5 text-title font-semibold tracking-tight">Вхід</h1>

        <div className="mt-4 flex flex-col gap-3">
          <label className="flex flex-col gap-1.5 text-xs text-muted">
            E-mail
            <input
              ref={emailRef}
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={busy}
              className="jv-input text-sm"
            />
          </label>
          <label className="flex flex-col gap-1.5 text-xs text-muted">
            Пароль
            <input
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={busy}
              className="jv-input text-sm"
            />
          </label>
        </div>

        {displayError && (
          <p role="alert" className="mt-3 text-xs leading-relaxed text-danger">
            {displayError}
          </p>
        )}

        <button
          type="submit"
          disabled={busy}
          className="jv-btn-primary mt-4 w-full justify-center"
        >
          {busy ? "Входжу…" : "Увійти"}
        </button>

        {providers.data?.google && (
          <>
            <div className="mt-4 flex items-center gap-2.5 text-micro text-muted">
              <span className="h-px flex-1 bg-line" aria-hidden="true" />
              або
              <span className="h-px flex-1 bg-line" aria-hidden="true" />
            </div>

            <button
              type="button"
              onClick={handleGoogleClick}
              disabled={busy}
              className="jv-btn mt-4 w-full justify-center"
            >
              <svg
                aria-hidden="true"
                viewBox="0 0 24 24"
                width="16"
                height="16"
                fill="currentColor"
              >
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.55c2.08-1.92 3.29-4.74 3.29-8.1z" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.55-2.77c-.98.66-2.24 1.06-3.73 1.06-2.87 0-5.3-1.94-6.17-4.53H2.18v2.85A11 11 0 0 0 12 23z" />
                <path d="M5.83 14.1A6.6 6.6 0 0 1 5.48 12c0-.73.13-1.44.35-2.1V7.05H2.18A11 11 0 0 0 1 12c0 1.77.43 3.45 1.18 4.95z" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1a11 11 0 0 0-9.82 6.05l3.65 2.85c.87-2.6 3.3-4.53 6.17-4.53z" />
              </svg>
              Увійти через Google
            </button>
          </>
        )}
      </form>
    </div>
  );
}
